import { useCallback, useMemo } from 'react'
import { useDebounceCallback } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import type { BillingInterval, ObPlanId } from '../../../gql/graphql'
import { useModalManager } from '../../../components/ModalManager/hooks/useModalManager'
import { useUser } from '../../../common/context/User'
import type { PlanSelectorCohort } from '../utils/cohortDetection'

// Minimal user data needed for tracking
export interface UserData {
  isImpersonation: boolean
  currentOrganization?: {
    id: string
    billing?: {
      isAutomaticTaxEnabled?: boolean | null
    } | null
    commonTrackingProperties?: {
      atEventBillingState?: string
      atEventBillingPlan?: string
      atEventBillingCycle?: string
      atEventBillingGateway?: string
      atEventIsNewBuffer?: boolean
      atEventChannelQty?: number
    } | null
  }
}

interface TrackingParams {
  cta: string
  ctaButton: string
  upgradePathName: string
}

interface CurrentPlan {
  planId: ObPlanId
  billingCycle: BillingInterval
  channelCount: number
}

interface PlanSelectorTrackingProps {
  currentUser: {
    currentPlan: CurrentPlan
    hasPaymentMethod: boolean
  }
  selection: {
    planId: ObPlanId
    billingCycle: BillingInterval
    channelCount: number
  }
  // Optional tracking props for when PlanSelector is embedded in a page
  cta?: string
  ctaButton?: string
  upgradePathName?: string
  // Optional user data for when used outside app-shell context
  user?: UserData
  // Plan selector version tracking (tiered, non-tiered, or agency)
  // This should always be explicitly defined based on the detected cohort
  planSelectorVersion: PlanSelectorCohort
}

interface UsePlanSelectorTrackingReturn {
  trackViewed: () => void
  trackChannelQuantityChange: (channelQuantity: number) => void
  trackBillingCycleChange: (
    cycle: BillingInterval,
    channelQuantity: number,
  ) => void
  trackPlanSelection: (planId: ObPlanId, channelQuantity: number) => void
}

export function usePlanSelectorTracking({
  currentUser,
  selection,
  cta: propCta,
  ctaButton: propCtaButton,
  upgradePathName: propUpgradePathName,
  user: userProp,
  planSelectorVersion,
}: PlanSelectorTrackingProps): UsePlanSelectorTrackingReturn {
  const userFromContext = useUser()
  // Prefer prop, then context; allow undefined and handle downstream
  const user = userProp ?? userFromContext
  const { modalData } = useModalManager()

  // Extract tracking parameters - prefer props over modal data
  const trackingParams: TrackingParams = useMemo(
    () => ({
      cta: propCta || modalData?.cta || '',
      ctaButton: propCtaButton || modalData?.ctaButton || '',
      upgradePathName: propUpgradePathName || modalData?.upgradePathName || '',
    }),
    [modalData, propCta, propCtaButton, propUpgradePathName],
  )

  // Check if we should track (not impersonating and has org)
  const canTrack = useMemo(() => {
    return Boolean(!user?.isImpersonation && user?.currentOrganization?.id)
  }, [user?.isImpersonation, user?.currentOrganization?.id])

  // Format plan string for tracking (e.g., "essentials month" -> "essentialsMonth")
  const formatPlanString = (planId: string, interval: string): string => {
    const combined = `${planId} ${interval}`
    return combined
      .split(/\s+/)
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join('')
  }

  // Get product from current path
  const getProduct = useCallback((): string => {
    const path = window.location.pathname
    if (path.includes('/publish')) return 'publish'
    if (path.includes('/analyze')) return 'analyze'
    if (path.includes('/engage')) return 'engage'
    if (path.includes('/start-page')) return 'start-page'
    return 'publish' // default
  }, [])

  const commonTrackingProperties = useMemo(
    () => user?.currentOrganization?.commonTrackingProperties || {},
    [user?.currentOrganization?.commonTrackingProperties],
  )

  // Track PlanSelector viewed on mount
  const trackViewed = useCallback(() => {
    if (canTrack) {
      const product = getProduct()
      const currentPlanFormatted = formatPlanString(
        currentUser.currentPlan.planId,
        currentUser.currentPlan.billingCycle,
      )

      BufferTracker.planSelectorViewed({
        organizationId: user?.currentOrganization?.id || '',
        ctaApp: product,
        ctaVersion: '1',
        currentPlan: currentPlanFormatted,
        ctaLocation: 'appShell',
        screenName: 'plans',
        ctaButton: trackingParams.ctaButton || null,
        initialChannelQuantity: currentUser.currentPlan.channelCount,
        initialCycle: selection.billingCycle,
        initialSelectedPlan: selection.planId,
        hasPaymentDetails: currentUser.hasPaymentMethod,
        cta: trackingParams.cta ? `${product}-${trackingParams.cta}` : null,
        isStripeCheckout: true,
        isAutomaticTaxEnabled:
          user?.currentOrganization?.billing?.isAutomaticTaxEnabled ?? false,
        upgradePathName: trackingParams.upgradePathName || null,
        planSelectorVersion,
        ...commonTrackingProperties,
      })
    }
  }, [
    canTrack,
    currentUser,
    selection,
    trackingParams,
    user,
    getProduct,
    planSelectorVersion,
  ])

  // Debounced tracking for channel quantity changes
  const trackChannelQuantityChange = useDebounceCallback(
    useCallback(
      (channelQuantity: number) => {
        if (canTrack) {
          BufferTracker.planSelectorChannelQuantityClicked({
            organizationId: user?.currentOrganization?.id || '',
            channelQuantity,
            hasPaymentDetails: currentUser.hasPaymentMethod,
            planSelectorVersion,
            ...commonTrackingProperties,
          })
        }
      },
      [
        canTrack,
        user?.currentOrganization?.id,
        currentUser.hasPaymentMethod,
        planSelectorVersion,
        commonTrackingProperties,
      ],
    ),
    900,
  )

  // Debounced tracking for billing cycle changes
  const trackBillingCycleChange = useDebounceCallback(
    useCallback(
      (cycle: BillingInterval, channelQuantity: number) => {
        if (canTrack) {
          BufferTracker.planSelectorPlanCycleClicked({
            organizationId: user?.currentOrganization?.id || '',
            cycle,
            channelQuantity,
            hasPaymentDetails: currentUser.hasPaymentMethod,
            planSelectorVersion,
            ...commonTrackingProperties,
          })
        }
      },
      [
        canTrack,
        user?.currentOrganization?.id,
        currentUser.hasPaymentMethod,
        planSelectorVersion,
        commonTrackingProperties,
      ],
    ),
    900,
  )

  // Track plan selection
  const trackPlanSelection = useCallback(
    (planId: ObPlanId, channelQuantity: number) => {
      if (canTrack) {
        const commonTrackingProperties =
          user?.currentOrganization?.commonTrackingProperties || {}

        BufferTracker.planSelectorPlanClicked({
          organizationId: user?.currentOrganization?.id || '',
          selectedPlanName: planId,
          channelQuantity,
          hasPaymentDetails: currentUser.hasPaymentMethod,
          planSelectorVersion,
          ...commonTrackingProperties,
        })
      }
    },
    [canTrack, user, currentUser, planSelectorVersion],
  )

  return {
    trackViewed,
    trackChannelQuantityChange,
    trackBillingCycleChange,
    trackPlanSelection,
  }
}
